import { RAW_MANUAL_SECTIONS } from "../data/manualSections";
import { FORM_ASSISTANT_GUIDES } from "../data/formAssistantGuides";
import { PROCESS_ASSISTANT_GUIDES } from "../data/processAssistantGuides";
import { STUDIO_ASSISTANT_GUIDES } from "../data/studioAssistantGuides";
import { ASSISTANT_ACTION_TYPES, resolveAssistantAction } from "./assistantActions";
import { buildVariedText } from "./textVariations";
import { evaluateArithmeticExpression } from "./safeExpression";

const createVariedReply = (text, options = {}) => buildVariedText(text, options);

const FRIENDLY_OPENERS = [
  (name) => (name ? `Oi, ${name}! Vamos juntos nessa?` : "Oi! Vamos juntos nessa?"),
  (name) => (name ? `${name}, vamos resolver isso de um jeito leve.` : "Vamos resolver isso de um jeito leve."),
  (name) => (name ? `${name}, deixa comigo que eu te guio por aqui.` : "Deixa comigo que eu te guio por aqui."),
  (name) => (name ? `${name}, partiu organizar isso agora?` : "Partiu organizar isso agora?"),
  (name) => (name ? `${name}, vamos destravar isso juntos.` : "Vamos destravar isso juntos."),
  (name) => (name ? `${name}, posso montar um caminho rápido pra isso.` : "Posso montar um caminho rápido pra isso."),
  (name) => (name ? `${name}, vamos alinhar isso sem complicação.` : "Vamos alinhar isso sem complicação."),
  (name) => (name ? `${name}, quer que eu já comece pelo ponto principal?` : "Quer que eu já comece pelo ponto principal?"),
  (name) => (name ? `${name}, vamos colocar ordem nisso agora.` : "Vamos colocar ordem nisso agora."),
  (name) => (name ? `${name}, pode contar comigo pra resolver isso.` : "Pode contar comigo pra resolver isso."),
  (name) => (name ? `${name}, vamos transformar isso em um plano claro.` : "Vamos transformar isso em um plano claro."),
  (name) => (name ? `Tudo bem por aí, ${name}?` : "Tudo bem por aí?"),
  (name) => (name ? `Que bom falar com você, ${name}!` : "Que bom falar com você!"),
];

const FRIENDLY_OUTROS = [
  "Se quiser ajustar algo, estou por aqui. 🤝",
  "Qualquer coisa é só chamar que sigo ajudando. ✨",
  "Curti ajudar! Me chama se precisar de outra ideia. 😊",
  "Fico por perto para aprofundar quando quiser. 🚀",
  "Se pintar outra dúvida, manda ver que eu acompanho. 💬",
];

const pickFriendlyTone = (userName = "") => {
  const firstName = extractFirstName(userName);
  const openerFactory = FRIENDLY_OPENERS[Math.floor(Math.random() * FRIENDLY_OPENERS.length)];
  const opener = openerFactory(firstName);
  const outro = FRIENDLY_OUTROS[Math.floor(Math.random() * FRIENDLY_OUTROS.length)];
  return { opener, outro };
};

const ASSISTANT_COLLABORATORS = [
  {
    id: "forms",
    keywords: ["formulario", "form", "formulário"],
    name: "Assistente de Formulários",
    capability:
      "estrutura campos, validações e preenchimentos automáticos, retornando o JSON pronto para o Studio publicar",
  },
  {
    id: "processes",
    keywords: ["processo", "fluxo", "bpmn"],
    name: "Assistente de Processos",
    capability:
      "gera a sequência de etapas, donos e SLAs, alinhando com gateways configuráveis e exportando o fluxo para o modelador",
  },
  {
    id: "studio",
    keywords: ["studio", "designer", "modelagem"],
    name: "Assistente IA do Studio",
    capability:
      "sincroniza assets, guias e automações para publicar versões, puxando dados do Manual e do CRM para enriquecer contextos",
  },
  {
    id: "manual",
    keywords: ["manual", "politica", "governanca"],
    name: "Assistente do Manual",
    capability:
      "garante conformidade e políticas aplicadas, anexando referências e termos que alimentam outros copilotos",
  },
  {
    id: "rpa",
    keywords: ["rpa", "automacao", "robos"],
    name: "Hub de Automações",
    capability:
      "dispara ou agenda robôs para registrar dados em sistemas externos, enviar notificações e consolidar logs no painel",
  },
];

const CREATION_VERBS = [
  "criar",
  "crie",
  "montar",
  "monta",
  "gerar",
  "gere",
  "construir",
  "desenhar",
  "formular",
  "preencher",
  "configurar",
  "elaborar",
  "definir",
  "planejar",
  "modelar",
  "sugerir",
  "novo",
  "nova",
  "preciso",
  "precisamos",
  "quero",
  "queremos",
  "solicito",
  "solicitamos",
];

const normalizeText = (value) =>
  (value || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const QUESTION_STARTERS = [
  "como",
  "onde",
  "quando",
  "qual",
  "quais",
  "quem",
  "por que",
  "porque",
  "pra que",
  "para que",
  "quanto",
  "quantos",
  "quantas",
  "pode",
  "poderia",
];

const isQuestionPrompt = (prompt = "") => {
  const raw = (prompt || "").trim();
  if (!raw) {
    return false;
  }
  if (/[?？]\s*$/.test(raw)) {
    return true;
  }
  const normalized = normalizeText(raw);
  return QUESTION_STARTERS.some((starter) =>
    normalized.startsWith(starter.replace(/\s+/g, " "))
  );
};

const classifyPromptIntent = (prompt = "") =>
  isQuestionPrompt(prompt) ? "question" : "command";

const STOPWORDS = new Set(
  [
    "de",
    "do",
    "da",
    "para",
    "por",
    "um",
    "uma",
    "novo",
    "nova",
    "novos",
    "novas",
    "criar",
    "crie",
    "criado",
    "criada",
    "criados",
    "criar",
    "criação",
    "gerar",
    "gerar",
    "gerado",
    "gerada",
    "montar",
    "montado",
    "montada",
    "montagem",
    "definir",
    "definido",
    "definida",
    "planejar",
    "planejado",
    "planejada",
    "preciso",
    "precisamos",
    "preciso",
    "quero",
    "desejo",
    "solicito",
    "solicitar",
    "processo",
    "processos",
    "fluxo",
    "fluxos",
    "workflow",
    "formularios",
    "formulario",
    "form",
    "formulários",
    "formulário",
    "modelo",
    "modelo",
    "modelo",
    "modelo",
    "modelo",
    "modelo",
    "modelar",
    "etapas",
    "etapa",
    "passos",
    "passo",
    "fase",
    "fases",
    "em",
    "até",
    "ate",
    "com",
    "sobre",
    "para",
    "dos",
    "nas",
    "nos",
    "das",
    "ao",
    "a",
    "o",
  ].map((word) => normalizeText(word))
);

const splitSentences = (text = "") =>
  text
    .split(/(?<=[.!?])\s+(?=[A-ZÀ-Ü0-9])/)
    .map((part) => part.trim())
    .filter(Boolean);

const normalizeHighlightLine = (line = "") =>
  line
    .replace(/^[-•]\s*/, "")
    .replace(/^\d+\.\s*/, "")
    .trim();

const buildStructuredReplyText = ({ text = "", topic = "", highlights = [] } = {}) => {
  const normalizedText = (text || "").trim();
  if (!normalizedText) {
    return "";
  }

  const sections = normalizedText
    .split(/\n{2,}/)
    .map((section) => section.trim())
    .filter(Boolean);

  const primarySection = sections.shift() || "";
  const sentences = splitSentences(primarySection);
  const headline = topic || sentences[0] || primarySection;

  const bulletSources = [...sections, ...sentences.slice(1), ...highlights];
  const bullets = bulletSources
    .flatMap((section) =>
      section
        .split(/\n+/)
        .map((line) => normalizeHighlightLine(line))
        .filter(Boolean)
    )
    .map((line) => `- ${line}`);

  if (!bullets.length) {
    const residual = normalizeHighlightLine(sentences.slice(1).join(" ").trim());
    return residual ? [headline, "", `- ${residual}`].join("\n") : headline;
  }

  return [headline, "", ...bullets].join("\n");
};

const containsKeyword = (text, keywords = []) =>
  keywords.some((keyword) => text.includes(normalizeText(keyword)));

const containsAnyTokens = (text, keywords = []) =>
  keywords.some((keyword) => text.includes(normalizeText(keyword)));

const extractFirstName = (value) => {
  if (!value || typeof value !== "string") {
    return "";
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.split(/\s+/)[0];
};

const formatTimeFromISO = (value) => {
  if (!value) {
    return null;
  }
  try {
    return new Date(value).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    return null;
  }
};

const describeDeadlineFromISO = (value) => {
  if (!value) {
    return "sem prazo definido";
  }
  try {
    const target = new Date(value).getTime();
    if (!Number.isFinite(target)) {
      return "prazo indefinido";
    }
    const diffMinutes = Math.round((target - Date.now()) / 60000);
    if (!Number.isFinite(diffMinutes)) {
      return "prazo indefinido";
    }
    if (diffMinutes === 0) {
      return "vence agora";
    }
    const prefix = diffMinutes < 0 ? "atrasado há" : "vence em";
    const absoluteMinutes = Math.abs(diffMinutes);
    if (absoluteMinutes < 60) {
      return `${prefix} ${absoluteMinutes} min`;
    }
    if (absoluteMinutes < 60 * 24) {
      return `${prefix} ${Math.round(absoluteMinutes / 60)}h`;
    }
    return `${prefix} ${Math.round(absoluteMinutes / (60 * 24))}d`;
  } catch (error) {
    return "prazo indefinido";
  }
};

const buildDatasetAvailabilityReply = (status) => {
  if (status === "loading" || status === "idle") {
    return {
      text: createVariedReply(
        "Ainda estou sincronizando com o banco de dados. Clique no botão “Atualizar” e tente novamente em instantes."
      ),
    };
  }
  if (status === "error") {
    return {
      text: createVariedReply("Não consegui acessar o banco agora. Use o botão “Atualizar” para tentar novamente."),
    };
  }
  return null;
};

const createSlug = (value) =>
  normalizeText(value)
    .split(" ")
    .filter(Boolean)
    .join("-")
    .slice(0, 60);

const FIELD_TYPE_LABELS = {
  "text-short": "Texto curto",
  "text-long": "Texto longo/parágrafo",
  select: "Lista de opções",
  number: "Número",
  currency: "Moeda",
  date: "Data",
  datetime: "Data e hora",
  email: "E-mail",
  phone: "Telefone",
  file: "Upload de arquivos",
  checklist: "Checklist",
  user: "Seleção de usuário",
  link: "URL",
};

const inferFieldType = (label) => {
  const normalized = normalizeText(label);
  if (!normalized) {
    return "text-short";
  }
  if (/upload|anexo|document|curriculo|arquivo/.test(normalized)) {
    return "file";
  }
  if (/checklist|lista de verificacao/.test(normalized)) {
    return "checklist";
  }
  if (/data e hora|hora prevista/.test(normalized)) {
    return "datetime";
  }
  if (/data|prazo/.test(normalized)) {
    return "date";
  }
  if (/email/.test(normalized)) {
    return "email";
  }
  if (/telefone|contato/.test(normalized)) {
    return "phone";
  }
  if (/valor|custo|orcamento|preco|estimativa/.test(normalized)) {
    return "currency";
  }
  if (/quantidade|numero|qtde/.test(normalized)) {
    return "number";
  }
  if (/status|formato|classificacao|tipo|opcao|decisao|parecer|prioridade|modelo/.test(normalized)) {
    return "select";
  }
  if (/gestor|responsavel|entrevistador|aprovador|participante|time/.test(normalized)) {
    return "user";
  }
  if (/descricao|justificativa|observacao|feedback|detalhes|objetivo/.test(normalized)) {
    return "text-long";
  }
  if (/site|url|link/.test(normalized)) {
    return "link";
  }
  return "text-short";
};

const FIELD_HINT_RULES = [
  {
    regex: /(lgpd|consentimento|privacidade)/i,
    hint: "Adicionar aceite formal de privacidade (checkbox)",
  },
  {
    regex: /(valor|orcamento|custo|preco)/i,
    hint: "Aplicar máscara monetária com 2 casas decimais",
  },
  {
    regex: /(prazo|data|agenda|sla)/i,
    hint: "Validar datas futuras e alertar atrasos",
  },
  {
    regex: /(upload|anexo|document)/i,
    hint: "Aceitar PDF, DOCX e imagens até 10MB",
  },
  {
    regex: /(telefone|contato)/i,
    hint: "Aplicar máscara dinâmica para DDD e internacional",
  },
  {
    regex: /(email)/i,
    hint: "Validar domínio corporativo automático",
  },
];

const FIELD_PREFILL_RULES = [
  {
    regex: /(solicitante|responsavel)/i,
    prefill: "Preencher automaticamente com o usuário logado",
  },
  {
    regex: /(gestor|aprovador)/i,
    prefill: "Sugerir hierarquia usando o Assistente de Processos",
  },
  {
    regex: /(centro de custo|cc|custo)/i,
    prefill: "Sincronizar centros de custo do ERP",
  },
  {
    regex: /(fornecedor)/i,
    prefill: "Listar fornecedores homologados pelo módulo de Compras",
  },
  {
    regex: /(vaga|cargo)/i,
    prefill: "Puxar catálogo de vagas usado no Assistente do Studio",
  },
];

const augmentFieldLabelWithContext = (label, contextLabel) => {
  if (!label || !contextLabel) {
    return label;
  }
  if (/(descrição|descricao|objetivo|detalhes|justificativa|feedback|parecer|contexto)/i.test(label)) {
    return `${label} (${contextLabel})`;
  }
  return label;
};

const inferFieldAttributes = (rawLabel, { contextLabel, prompt } = {}) => {
  const adjustedLabel = augmentFieldLabelWithContext(rawLabel, contextLabel);
  const type = inferFieldType(adjustedLabel);
  const typeLabel = FIELD_TYPE_LABELS[type] || FIELD_TYPE_LABELS["text-short"];
  const hints = FIELD_HINT_RULES.filter((rule) => rule.regex.test(adjustedLabel)).map((rule) => rule.hint);
  const prefillRule = FIELD_PREFILL_RULES.find((rule) => rule.regex.test(adjustedLabel));
  const required = /(obrigat[oó]rio|necess[aá]rio)/i.test(rawLabel);
  const contextDerivedRequirement = prompt && /aprov/i.test(prompt) && /(parecer|aprova)/i.test(adjustedLabel);
  const metadata = {
    label: adjustedLabel,
    type,
    typeLabel,
    required: required || Boolean(contextDerivedRequirement),
    hint: hints.length > 0 ? hints.join(" | ") : null,
    prefill: prefillRule ? prefillRule.prefill : null,
    id: createSlug(adjustedLabel) || `campo-${Math.random().toString(36).slice(2, 6)}`,
  };
  return metadata;
};

const hasCreationIntent = (normalizedPrompt) => {
  if (!normalizedPrompt) {
    return false;
  }
  return CREATION_VERBS.some((verb) => normalizedPrompt.includes(normalizeText(verb)));
};

const selectCollaborators = (normalizedPrompt) => {
  if (!normalizedPrompt) {
    return ASSISTANT_COLLABORATORS;
  }
  const matches = ASSISTANT_COLLABORATORS.filter((collaborator) =>
    collaborator.keywords.some((keyword) => normalizedPrompt.includes(normalizeText(keyword)))
  );
  return matches.length ? matches : ASSISTANT_COLLABORATORS;
};

const buildCollaborationPlan = (normalizedPrompt, topicLabel) => {
  if (!hasCreationIntent(normalizedPrompt)) {
    return null;
  }
  const collaborators = selectCollaborators(normalizedPrompt);
  const phases = collaborators.map(
    (collaborator, index) =>
      `${index + 1}. ${collaborator.name}: ${collaborator.capability} (handoff direto com Nash Chat).`
  );
  const structuredPlan = {
    topic: topicLabel,
    orchestrator: "Nash IA",
    collaborators: collaborators.map((collaborator) => ({
      id: collaborator.id,
      name: collaborator.name,
      capability: collaborator.capability,
    })),
  };
  const textParts = [
    "Orquestração multiassistente ativada: a Nash IA usa o que cada copiloto já sabe para entregar mais rápido.",
    phases.join("\n"),
    "Os dados fluem entre Studio, Manual, RPA e CRM sem você precisar repetir informações.",
  ];
  return { text: textParts.join("\n"), structuredPlan };
};

const buildFormJsonPreview = (structuredForm) => {
  if (!structuredForm) {
    return "";
  }
  const preview = {
    title: structuredForm.title,
    focus: structuredForm.focus,
    groups: structuredForm.groups.map((group) => ({
      name: group.name,
      fields: group.fields.map((field) => ({
        id: field.id,
        label: field.label,
        type: field.type,
        required: field.required,
      })),
    })),
    validations: structuredForm.validations,
    automations: structuredForm.automations,
  };
  return JSON.stringify(preview, null, 2);
};

const PROCESS_OWNER_RULES = [
  { regex: /(cotaca|fornecedor|compra)/, owner: "Equipe de Compras" },
  { regex: /(aprova|gestor|diretoria|comite)/, owner: "Gestor responsável" },
  { regex: /(entrevista|talento|onboarding|admiss)/, owner: "Recursos Humanos" },
  { regex: /(financeiro|pagamento|orcamento|custo)/, owner: "Financeiro" },
  { regex: /(analise|triagem|cadastro|qualificacao)/, owner: "Time Operacional" },
];

const detectProcessOwner = (stepDescription) => {
  const normalized = normalizeText(stepDescription);
  if (!normalized) {
    return "Time do processo";
  }
  const matchedRule = PROCESS_OWNER_RULES.find((rule) => rule.regex.test(normalized));
  return matchedRule ? matchedRule.owner : "Time do processo";
};

const buildProcessJsonPreview = (structuredProcess) => {
  if (!structuredProcess) {
    return "";
  }
  const preview = {
    title: structuredProcess.title,
    steps: structuredProcess.steps.map((step) => ({
      order: step.order,
      description: step.description,
      owner: step.owner,
    })),
    formHighlights: structuredProcess.formHighlights,
    automations: structuredProcess.automations,
  };
  return JSON.stringify(preview, null, 2);
};

const VACATION_KEYWORDS = ["feria", "ferias", "vacation", "folga"];
const PROCESS_SUBJECT_KEYWORDS = [
  "process",
  "processo",
  "processos",
  "fluxo",
  "fluxos",
  "instancia",
  "instancias",
  "instância",
  "instâncias",
  "solicitacao",
  "solicitacoes",
  "solicitação",
  "solicitações",
  "tarefa",
  "tarefas",
];
const PROCESS_STATUS_KEYWORDS = [
  "abert",
  "ativo",
  "ativa",
  "ativos",
  "ativas",
  "penden",
  "andament",
  "aguard",
  "fila",
  "atras",
];
const LIST_KEYWORDS = [
  "lista",
  "listar",
  "quais",
  "quantos",
  "quantas",
  "mostrar",
  "mostre",
  "me diga",
  "diga",
  "conte",
  "relacione",
];
const PROCESS_MINE_KEYWORDS = [
  "minh",
  "comigo",
  "meu",
  "meus",
  "minha",
  "minhas",
  "atribuida",
  "atribuido",
  "atribuídas",
  "atribuídos",
];
const PROCESS_CLAIM_KEYWORDS = [
  "assumir",
  "assumo",
  "sem respons",
  "sem responsa",
  "disponivel",
  "disponivel",
  "disponiveis",
  "disponíveis",
  "aguardando respons",
];

const buildVacationDatasetReply = (dataset, normalizedPrompt, options) => {
  if (!containsAnyTokens(normalizedPrompt, VACATION_KEYWORDS)) {
    return null;
  }
  if (!dataset || options.datasetStatus !== "ready") {
    return (
      buildDatasetAvailabilityReply(options.datasetStatus) || {
        text: createVariedReply(
          "Ainda estou sincronizando com o banco de dados. Clique no botão “Atualizar” para tentar novamente."
        ),
      }
    );
  }
  const hrData = dataset?.humanResources;
  if (!hrData?.available) {
    return {
      text: createVariedReply(
        hrData?.reason ||
          "A Nash IA não tem autorização para consultar férias com o seu usuário."
      ),
      topic: "Férias dos colaboradores",
    };
  }
  const employees = hrData.vacationEmployees || [];
  if (!employees.length) {
    return {
      text: createVariedReply(
        "Nenhum colaborador aparece como “Em férias” neste momento no cadastro do RH."
      ),
      topic: "Férias dos colaboradores",
    };
  }
  const { opener, outro } = pickFriendlyTone(options?.user?.name || "");
  const limited = employees.slice(0, 8);
  const lines = limited.map((employee, index) => {
    const department = employee.department ? ` · ${employee.department}` : "";
    const position = employee.position ? ` (${employee.position})` : "";
    const delegateList = (employee.vacationDelegates || [])
      .map((delegate) => delegate.name)
      .join(", ");
    const delegates = delegateList
      ? `**Delegados:** ${delegateList}`
      : "**Delegados:** não informados";
    const details = [
      `**${index + 1}. ${employee.fullName}${position}${department}**`,
      delegates,
    ]
      .filter(Boolean)
      .join("\n   • ");
    return `- ${details}`;
  });
  const remaining = employees.length - limited.length;
  const extraLine =
    remaining > 0
      ? `Exibindo ${limited.length} de ${employees.length} registros.`
      : "";
  const updatedLine = dataset.generatedAt
    ? `Dados atualizados às ${formatTimeFromISO(dataset.generatedAt) || "--:--"}.`
    : "";
  const body = createVariedReply(
    [
      `📌 **${
        employees.length === 1
          ? "1 colaborador em férias"
          : `${employees.length} colaboradores em férias`
      }:**`,
      lines.join("\n\n"),
      extraLine,
      updatedLine,
    ]
      .filter(Boolean)
      .join("\n\n"),
    { disableConnectors: true }
  );

  return {
    text: [opener, body, outro].filter(Boolean).join("\n\n"),
    topic: "Férias dos colaboradores",
    skipPersonalization: true,
  };
};

const buildProcessDatasetReply = (dataset, normalizedPrompt, options) => {
  const mentionsProcess = containsAnyTokens(normalizedPrompt, PROCESS_SUBJECT_KEYWORDS);
  const mentionsStatus =
    containsAnyTokens(normalizedPrompt, PROCESS_STATUS_KEYWORDS) ||
    containsAnyTokens(normalizedPrompt, LIST_KEYWORDS);
  if (!mentionsProcess || !mentionsStatus) {
    return null;
  }
  const openRequestsAction = {
    type: ASSISTANT_ACTION_TYPES.OPEN_TAB,
    tabId: "requests",
    tabLabel: "Minhas solicitações",
  };
  if (!dataset || options.datasetStatus !== "ready") {
    return (
      buildDatasetAvailabilityReply(options.datasetStatus) || {
        text: createVariedReply(
          "Ainda estou sincronizando com o banco de dados. Clique no botão “Atualizar” para tentar novamente."
        ),
        action: openRequestsAction,
      }
    );
  }
  const processData = dataset?.processes?.openInstances || [];
  if (!processData.length) {
    return {
      text: createVariedReply("Não encontrei processos em andamento no momento."),
      topic: "Processos em andamento",
      action: openRequestsAction,
    };
  }

  let filtered = processData;
  let contextLabel = "em andamento";
  if (containsAnyTokens(normalizedPrompt, PROCESS_MINE_KEYWORDS)) {
    filtered = filtered.filter((instance) => instance.isCurrentAssignee);
    contextLabel = "atribuídos a você";
  } else if (containsAnyTokens(normalizedPrompt, PROCESS_CLAIM_KEYWORDS)) {
    filtered = filtered.filter(
      (instance) => instance.canClaim && !instance.currentAssignee
    );
    contextLabel = "sem responsável";
  }

  if (!filtered.length) {
    return {
      text: createVariedReply(`Nenhuma instância ${contextLabel} foi encontrada agora mesmo.`),
      topic: "Processos em andamento",
      action: openRequestsAction,
    };
  }

  const { opener, outro } = pickFriendlyTone(options?.user?.name || "");
  const limited = filtered.slice(0, 6);
  const lines = limited.map((instance, index) => {
    const assignee = instance.currentAssignee?.name
      ? `**Responsável:** ${instance.currentAssignee.name}`
      : instance.canClaim
      ? "**Responsável:** disponível para assumir"
      : "**Responsável:** não definido";
    const deadline = describeDeadlineFromISO(instance.currentStepDeadline);
    const highlight = instance.dashboardHighlight
      ? ` • ${instance.dashboardHighlight.label}: ${instance.dashboardHighlight.value}`
      : "";
    return [
      `- **${index + 1}. ${instance.processName}**`,
      `  • **Etapa:** ${instance.currentStepName}${highlight}`,
      `  • ${assignee}`,
      `  • **Prazo:** ${deadline}`,
    ].join("\n");
  });

  const footerParts = [];
  if (filtered.length > limited.length) {
    footerParts.push(`Mostrando ${limited.length} de ${filtered.length} instâncias.`);
  }
  if (dataset.generatedAt) {
    footerParts.push(
      `Atualizado às ${formatTimeFromISO(dataset.generatedAt) || "--:--"}.`
    );
  }
  const firstName = extractFirstName(options?.user?.name || "");
  const introPrefix = firstName ? `${firstName}, ` : "";

  const body = createVariedReply(
    [
      `${introPrefix}**encontrei ${
        filtered.length === 1
          ? "1 instância"
          : `${filtered.length} instâncias`
      } ${contextLabel}:**`,
      lines.join("\n\n"),
      footerParts.join(" ").trim(),
    ]
      .filter(Boolean)
      .join("\n\n"),
    { disableConnectors: true }
  );

  return {
    text: [opener, body, outro].filter(Boolean).join("\n\n"),
    topic: "Processos em andamento",
    action: openRequestsAction,
    skipPersonalization: true,
  };
};

const getDatasetReply = (prompt, normalizedPrompt, dataset, options = {}) => {
  return (
    buildVacationDatasetReply(dataset, normalizedPrompt, options) ||
    buildProcessDatasetReply(dataset, normalizedPrompt, options)
  );
};

const buildContextLabel = (prompt, extraStopwords = []) => {
  const normalizedExtra = new Set(extraStopwords.map((word) => normalizeText(word)));
  const tokens = (prompt || "")
    .split(/[^\p{L}\p{N}]+/u)
    .map((token) => token.trim())
    .filter(Boolean);
  const filtered = tokens.filter((token) => {
    const normalized = normalizeText(token);
    if (!normalized) {
      return false;
    }
    if (STOPWORDS.has(normalized)) {
      return false;
    }
    if (normalizedExtra.has(normalized)) {
      return false;
    }
    return true;
  });
  const context = filtered.join(" ");
  if (!context) {
    return "cenário informado";
  }
  return context.charAt(0).toUpperCase() + context.slice(1);
};

const FORM_BLUEPRINT_LIBRARY = [
  {
    id: "interview",
    keywords: ["entrevista", "recrut", "talento", "seleca", "candidato"],
    title: "Entrevistas e recrutamento",
    groups: [
      {
        name: "Perfil do candidato",
        fields: [
          "Nome completo (obrigatório)",
          "Vaga/cargo desejado",
          "Contato (e-mail e telefone)",
          "Canal de origem (LinkedIn, indicação, etc.)",
        ],
      },
      {
        name: "Detalhes da entrevista",
        fields: [
          "Formato (presencial, remoto ou híbrido)",
          "Data e horário sugeridos",
          "Entrevistadores envolvidos",
          "Competências a serem avaliadas",
        ],
      },
      {
        name: "Feedback e decisão",
        fields: [
          "Notas por critério (cultural, técnico, comunicação)",
          "Parecer final (Aprovado, Banco de talentos, Não seguir)",
          "Checklist de documentos pendentes",
          "Próximos passos acordados",
        ],
      },
    ],
    validations: [
      "Campos de contato obrigatórios e validados com máscara",
      "Checklist de documentos somente visível para candidatos pré-aprovados",
      "Habilitar upload de currículo e portfólio quando solicitado",
    ],
    automations: [
      "Notificar RH e gestor imediato após envio do feedback",
      "Atualizar a etapa do candidato no pipeline automaticamente",
      "Compartilhar resumo com o Assistente do Studio para gerar ajustes no processo",
    ],
  },
  {
    id: "procurement",
    keywords: ["compra", "compras", "aquisicao", "fornecedor", "suprimento"],
    title: "Compras e fornecedores",
    groups: [
      {
        name: "Solicitação",
        fields: [
          "Solicitante e centro de custo",
          "Descrição do item ou serviço",
          "Justificativa de negócio",
          "Valor estimado e moeda",
        ],
      },
      {
        name: "Fornecedores",
        fields: [
          "Fornecedor preferencial",
          "Anexos de propostas",
          "Prazo de entrega",
          "Critérios de homologação",
        ],
      },
      {
        name: "Aprovação",
        fields: [
          "Alçadas envolvidas",
          "Checklist de compliance",
          "Classificação fiscal",
          "Parecer final",
        ],
      },
    ],
    validations: [
      "Valor estimado obrigatório com duas casas decimais",
      "Anexo de proposta exigido acima de um limite configurável",
      "Campo de compliance condicionado ao tipo de fornecedor",
    ],
    automations: [
      "Acionar IA de Processos para sugerir etapas de cotação",
      "Enviar resumo para Financeiro e Estoque",
      "Gerar tarefa automática para anexar pedido de compra quando aprovado",
    ],
  },
  {
    id: "expense-reimbursement",
    keywords: [
      "reembolso",
      "ressarcimento",
      "adiantamento",
      "despesa",
      "viagem",
      "refund",
    ],
    title: "Solicitação de reembolso",
    groups: [
      {
        name: "Dados do solicitante",
        fields: [
          "Nome do colaborador e matrícula (obrigatório)",
          "Centro de custo/área responsável",
          "Gestor aprovador",
          "Forma de pagamento preferencial (PIX, depósito, folha)",
        ],
      },
      {
        name: "Detalhes da despesa",
        fields: [
          "Tipo da despesa (transporte, alimentação, hospedagem, ferramenta)",
          "Data e local do gasto",
          "Valor total e moeda",
          "Projeto ou viagem associada",
          "Justificativa do reembolso",
          "Anexar comprovantes/fotos (obrigatório)",
        ],
      },
      {
        name: "Regras e aprovação",
        fields: [
          "Política aplicada (diárias, limites, exceções)",
          "Necessita aprovação extra?",
          "Responsável financeiro",
          "Dados bancários para crédito",
        ],
      },
    ],
    validations: [
      "Valor total obrigatório e maior que zero",
      "Comprovantes exigidos acima de um valor mínimo configurável",
      "Aviso automático quando o tipo de despesa foge da política",
    ],
    automations: [
      "Preencher dados do colaborador via IA do Manual e cadastro corporativo",
      "Gerar etapa de aprovação no Assistente de Processos quando houver exceção",
      "Disparar robô RPA para enviar comprovantes ao financeiro e registrar no ERP",
    ],
  },
  {
    id: "onboarding",
    keywords: ["onboarding", "admissao", "integracao", "contratacao"],
    title: "Onboarding de colaboradores",
    groups: [
      {
        name: "Dados pessoais",
        fields: [
          "Nome completo e documento",
          "Endereço",
          "Dados bancários",
          "Contatos de emergência",
        ],
      },
      {
        name: "Detalhes da contratação",
        fields: [
          "Cargo e gestor",
          "Data de início",
          "Modelo de trabalho",
          "Benefícios selecionados",
        ],
      },
      {
        name: "Checklist de entrega",
        fields: [
          "Equipamentos e acessos",
          "Treinamentos obrigatórios",
          "Status do kit de boas-vindas",
          "Observações do RH",
        ],
      },
    ],
    validations: [
      "Dados bancários condicionados ao país",
      "Checklist obrigatório antes da data de início",
      "Campos LGPD com consentimento explícito",
    ],
    automations: [
      "Integrar com o Assistente de Formulários para criar grupos automaticamente",
      "Acionar robôs RPA para provisionar acessos",
      "Enviar lembretes para o gestor completar o checklist",
    ],
  },
  {
    id: "generic",
    keywords: [],
    title: "Formulário inteligente",
    groups: [
      {
        name: "Contexto geral",
        fields: [
          "Responsável pela abertura",
          "Objetivo",
          "Datas importantes",
          "Stakeholders envolvidos",
        ],
      },
      {
        name: "Detalhamento",
        fields: [
          "Descrição estruturada",
          "Indicadores que serão acompanhados",
          "Documentos de apoio",
          "Requisitos de aprovação",
        ],
      },
      {
        name: "Encerramento",
        fields: [
          "Checklist final",
          "Lições aprendidas",
          "Anexos obrigatórios",
          "Confirmação do responsável",
        ],
      },
    ],
    validations: [
      "Campos obrigatórios por grupo",
      "Validação automática de datas",
      "Uploads limitados por tipo de arquivo",
    ],
    automations: [
      "Sincronizar com o Assistente do Manual para exibir políticas",
      "Acionar IA de Processos para gerar fluxo correspondente",
      "Notificar interessados quando campos críticos forem alterados",
    ],
  },
];

const PROCESS_BLUEPRINT_LIBRARY = [
  {
    id: "procurement",
    keywords: ["compra", "compras", "aquisicao", "procurement", "cotacao", "fornecedor"],
    title: "Processo de compras",
    steps: [
      "Abertura da requisição com formulário detalhado e centro de custo",
      "Triagem do requisitante e validação de orçamento",
      "Cotação com fornecedores e anexos das propostas",
      "Aprovação financeira e compliance",
      "Emissão do pedido, integração com Estoque/Financeiro e registro da entrega",
    ],
    formHighlights: [
      "Grupo de identificação do solicitante e centro de custo",
      "Campos para especificações técnicas e quantidade",
      "Uploads obrigatórios de comparativos de preço",
      "Checklist de aprovação e campos de parecer",
    ],
    automationTips: [
      "Acionar robô de cotação a partir da etapa 3",
      "Enviar alerta pro Financeiro quando houver SLA expirando",
      "Gerar documento final usando o assistente do Studio",
    ],
  },
  {
    id: "recruitment",
    keywords: ["recrut", "entrevista", "admissao", "talento", "vaga"],
    title: "Processo de recrutamento",
    steps: [
      "Cadastro da vaga e requisitos",
      "Triagem e shortlist de candidatos",
      "Entrevistas e avaliação técnica",
      "Decisão e aprovação do gestor",
      "Onboarding e documentação",
    ],
    formHighlights: [
      "Campos para requisitos obrigatórios e desejáveis",
      "Registro das entrevistas com notas",
      "Uploads de documentos",
      "Checklist de admissão",
    ],
    automationTips: [
      "Sincronizar com o formulário de entrevista sugerido",
      "Acionar notificações no Nash Chat para os avaliadores",
      "Gerar relatórios de pipeline automaticamente",
    ],
  },
  {
    id: "generic",
    keywords: [],
    title: "Fluxo operacional",
    steps: [
      "Registro da solicitação e classificação",
      "Análise e coleta de informações",
      "Execução ou aprovação intermediária",
      "Validação de resultado",
      "Encerramento com registro de indicadores",
    ],
    formHighlights: [
      "Campos de objetivo e impacto",
      "Checklist de documentos",
      "Definição de responsáveis",
      "Campos de lições aprendidas",
    ],
    automationTips: [
      "Conectar com o Assistente do Manual para exibir políticas",
      "Acionar IA do Studio para gerar variações no BPMN",
      "Programar lembretes recorrentes na Agenda",
    ],
  },
];

const FORM_KEYWORDS = ["formulario", "formularios", "form", "formulário", "formulários"];
const PROCESS_KEYWORDS = ["processo", "processos", "fluxo", "fluxos", "workflow", "bpmn"];

const buildFormBlueprintReply = (prompt, normalizedPrompt) => {
  if (!containsKeyword(normalizedPrompt, FORM_KEYWORDS)) {
    return null;
  }
  if (!containsKeyword(normalizedPrompt, CREATION_VERBS)) {
    return null;
  }
  const template =
    FORM_BLUEPRINT_LIBRARY.find((entry) =>
      entry.keywords.some((keyword) => normalizedPrompt.includes(keyword))
    ) || FORM_BLUEPRINT_LIBRARY[FORM_BLUEPRINT_LIBRARY.length - 1];
  const contextLabel = buildContextLabel(prompt, FORM_KEYWORDS);
  const blueprintTitle = `Formulário para ${contextLabel}`;
  const structuredForm = {
    type: "form",
    title: blueprintTitle,
    focus: template.title,
    groups: template.groups.map((group) => ({
      name: group.name,
      fields: group.fields.map((field, index) => ({
        order: index + 1,
        ...inferFieldAttributes(field, { contextLabel, prompt }),
      })),
    })),
    validations: template.validations,
    automations: template.automations,
  };
  const groupLines = structuredForm.groups
    .map((group) => {
      const header = `• ${group.name}`;
      const fieldLines = group.fields
        .map((field) => {
          const items = [field.typeLabel, field.required ? "obrigatório" : "opcional"];
          if (field.hint) {
            items.push(field.hint);
          }
          if (field.prefill) {
            items.push(`Pré-preenchimento: ${field.prefill}`);
          }
          return `  ${field.order}. ${field.label} — ${items.join(" · ")}`;
        })
        .join("\n");
      return [header, fieldLines].filter(Boolean).join("\n");
    })
    .join("\n");
  const validationLines = structuredForm.validations
    .map((rule, index) => `${index + 1}. ${rule}`)
    .join("\n");
  const automationLines = structuredForm.automations.map((tip) => `- ${tip}`).join("\n");
  const jsonPreview = buildFormJsonPreview(structuredForm);
  const collaborationPlan = buildCollaborationPlan(normalizedPrompt, blueprintTitle);
  const textParts = [
    `Estrutura completa gerada pela Nash IA do Nash Chat utilizando os mesmos mecanismos do Assistente IA de Formulários: ${blueprintTitle}.`,
    `Foco principal: ${template.title}.`,
    "Grupos e campos preenchidos automaticamente:",
    groupLines,
    "",
    "Validações e regras aplicadas:",
    validationLines,
    "",
    "Automação e colaboração com outras IAs:",
    automationLines,
    "",
    "Prévia estruturada para colar direto no construtor:",
    jsonPreview,
    "",
    collaborationPlan ? collaborationPlan.text : null,
    collaborationPlan ? "Cada copiloto compartilha atributos, máscaras e políticas para acelerar a publicação." : null,
    "",
    "Sempre que você treina o Assistente do Studio, o Manual ou os especialistas de processos, esta IA também aprende automaticamente e consegue preencher os campos para você.",
  ].filter(Boolean);
  return {
    text: createVariedReply(textParts.join("\n")),
    topic: blueprintTitle,
    structuredResult: {
      ...structuredForm,
      collaboration: collaborationPlan?.structuredPlan || null,
    },
  };
};

const buildProcessBlueprintReply = (prompt, normalizedPrompt) => {
  if (!containsKeyword(normalizedPrompt, PROCESS_KEYWORDS)) {
    return null;
  }
  if (!containsKeyword(normalizedPrompt, CREATION_VERBS)) {
    return null;
  }
  const template =
    PROCESS_BLUEPRINT_LIBRARY.find((entry) =>
      entry.keywords.some((keyword) => normalizedPrompt.includes(keyword))
    ) || PROCESS_BLUEPRINT_LIBRARY[PROCESS_BLUEPRINT_LIBRARY.length - 1];
  const contextLabel = buildContextLabel(prompt, PROCESS_KEYWORDS);
  const blueprintTitle = `Processo para ${contextLabel}`;
  const stepMatch = normalizedPrompt.match(/(\d+)\s*(?:etapa|passo|fase)s?/);
  const desiredStepCount = stepMatch ? Math.max(3, Math.min(10, Number(stepMatch[1]) || 5)) : 5;
  const steps = template.steps.slice(0, desiredStepCount);
  while (steps.length < desiredStepCount) {
    steps.push(
      `Etapa ${steps.length + 1}: ajuste a regra final considerando integrações e monitoramento.`,
    );
  }
  const structuredProcess = {
    type: "process",
    title: blueprintTitle,
    steps: steps.map((step, index) => ({
      order: index + 1,
      description: step,
      owner: detectProcessOwner(step),
      handoff: index > 0 ? `Transição da etapa ${index} para ${index + 1}` : "Início do processo",
    })),
    formHighlights: template.formHighlights,
    automations: template.automationTips,
  };
  const stepLines = structuredProcess.steps
    .map(
      (step) =>
        `${step.order}. ${step.description} — Responsável: ${step.owner}${step.handoff ? ` · ${step.handoff}` : ""}`
    )
    .join("\n");
  const formLines = structuredProcess.formHighlights.map((item, index) => `${index + 1}. ${item}`).join("\n");
  const automationLines = structuredProcess.automations.map((tip) => `- ${tip}`).join("\n");
  const jsonPreview = buildProcessJsonPreview(structuredProcess);
  const collaborationPlan = buildCollaborationPlan(normalizedPrompt, blueprintTitle);
  const textParts = [
    `Fluxo sugerido e preenchido pela Nash IA do Nash Chat, reutilizando os mesmos algoritmos do Assistente de Processos do Studio: ${blueprintTitle}.`,
    `Número de etapas: ${desiredStepCount}.`,
    "Sequência proposta com responsáveis:",
    stepLines,
    "",
    "Formulário inicial recomendado:",
    formLines,
    "",
    "Automação e insights compartilhados com outras IAs:",
    automationLines,
    "",
    "Prévia estruturada para importar no Studio ou compartilhar no Manual:",
    jsonPreview,
    "",
    collaborationPlan ? collaborationPlan.text : null,
    collaborationPlan
      ? "Reaproveito o que já foi ensinado aos copilotos de Formulários, Processos, Manual e RPA para publicar e acompanhar o fluxo sem retrabalho."
      : null,
    "",
    "Tudo que você alimenta nas IAs do Studio, Manual e Formulários fortalece automaticamente este copiloto no Nash Chat, por isso consigo preencher cada etapa para você.",
  ];
  return {
    text: createVariedReply(textParts.join("\n")),
    topic: blueprintTitle,
    structuredResult: {
      ...structuredProcess,
      collaboration: collaborationPlan?.structuredPlan || null,
    },
  };
};

const resolveBlueprintReply = (prompt, normalizedPrompt) =>
  buildFormBlueprintReply(prompt, normalizedPrompt) ||
  buildProcessBlueprintReply(prompt, normalizedPrompt);

const buildCollaborationOnlyReply = (prompt, normalizedPrompt) => {
  const explicitCollaborationIntent = /(multiassistente|orquestra|copiloto|copilotos|handoff)/i.test(
    normalizedPrompt || ""
  );
  if (!explicitCollaborationIntent) {
    return null;
  }
  const collaborationPlan = buildCollaborationPlan(normalizedPrompt, prompt || "sua entrega");
  if (!collaborationPlan) {
    return null;
  }
  return {
    text: createVariedReply(
      [
        collaborationPlan.text,
        "Eu mesmo aciono os copilotos do Studio, Manual e RPA e devolvo tudo consolidado aqui no Nash Chat.",
      ].join("\n")
    ),
    topic: "Orquestração multiassistente",
    structuredResult: collaborationPlan.structuredPlan,
  };
};

const buildKeywordSet = (keywords = []) => {
  const tokens = new Set();
  keywords.forEach((keyword) => {
    const normalized = normalizeText(keyword);
    if (!normalized) {
      return;
    }
    normalized.split(" ").forEach((token) => {
      if (token) {
        tokens.add(token);
      }
    });
  });
  return tokens;
};

const PAGE_CONTEXT_SCREEN_WORDS = new Set(
  ["tela", "pagina", "painel", "dashboard", "aba", "menu", "card", "bloco", "modulo", "área"].map((word) =>
    normalizeText(word)
  )
);
const PAGE_CONTEXT_REFERENCE_WORDS = new Set(
  [
    "aqui",
    "agora",
    "esta",
    "essa",
    "esse",
    "este",
    "nesta",
    "nesse",
    "nessa",
    "neste",
    "atual",
    "daqui",
  ].map((word) => normalizeText(word))
);
const PAGE_CONTEXT_NAVIGATION_WORDS = new Set(
  [
    "onde",
    "navegar",
    "encontrar",
    "achar",
    "localizar",
    "abrir",
    "acessar",
    "mostrar",
    "mostre",
    "explicar",
    "explica",
  ].map((word) => normalizeText(word))
);

const buildTextTokenSet = (value) => {
  const normalized = normalizeText(value);
  if (!normalized) {
    return new Set();
  }
  return new Set(normalized.split(" ").filter(Boolean));
};

const buildPageContextSources = (pageContext) => {
  if (!pageContext) {
    return [];
  }
  const sources = [];
  (pageContext.sections || []).forEach((text) => {
    if (!text) return;
    sources.push({ type: "section", text, tokenSet: buildTextTokenSet(text) });
  });
  (pageContext.highlights || []).forEach((text) => {
    if (!text) return;
    sources.push({ type: "highlight", text, tokenSet: buildTextTokenSet(text) });
  });
  (pageContext.actions || []).forEach((text) => {
    if (!text) return;
    sources.push({ type: "action", text, tokenSet: buildTextTokenSet(text) });
  });
  return sources;
};

const buildPageContextReply = (prompt, normalizedPrompt, pageContext, options = {}) => {
  if (!pageContext) {
    return null;
  }
  const { action = null } = options;
  const promptTokens = normalizedPrompt.split(" ").filter(Boolean);
  if (!promptTokens.length) {
    return null;
  }

  const hasScreenReference = promptTokens.some((token) => PAGE_CONTEXT_SCREEN_WORDS.has(token));
  const hasContextReference = promptTokens.some((token) =>
    PAGE_CONTEXT_REFERENCE_WORDS.has(token) || token.startsWith("nest") || token.startsWith("ness")
  );
  const wantsNavigation = promptTokens.some((token) => PAGE_CONTEXT_NAVIGATION_WORDS.has(token));
  const shortLocationQuestion =
    normalizedPrompt.length <= 32 && (normalizedPrompt.includes("aqui") || normalizedPrompt.includes("onde"));

  if (action && wantsNavigation && !hasContextReference && !shortLocationQuestion) {
    return null;
  }

  const sources = buildPageContextSources(pageContext);
  const rankedSources = sources
    .map((source) => {
      let score = 0;
      promptTokens.forEach((token) => {
        if (source.tokenSet.has(token)) {
          score += 1;
        }
      });
      if (score > 0 && source.type === "section") {
        score += 0.5;
      }
      return { ...source, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  const hasStrongMatch = rankedSources.length > 0 && rankedSources[0].score >= 2;
  const shouldExplainContext = (hasScreenReference && hasContextReference) || shortLocationQuestion;

  if (!shouldExplainContext && !hasStrongMatch && !shortLocationQuestion) {
    return null;
  }

  const matches = rankedSources.slice(0, shouldExplainContext ? 3 : 2);
  const summaryParts = [];
  if (pageContext.title || pageContext.path) {
    const titleLabel = pageContext.title || "tela atual";
    const pathLabel = pageContext.path ? ` (rota ${pageContext.path})` : "";
    summaryParts.push(`Você está visualizando ${titleLabel}${pathLabel}.`);
  }

  if (matches.length) {
    matches.forEach((match) => {
      const prefix =
        match.type === "action"
          ? "Ação disponível"
          : match.type === "section"
          ? "Seção em destaque"
          : "Informação visível";
      summaryParts.push(`${prefix}: ${match.text}`);
    });
  } else if (pageContext.summaryText) {
    summaryParts.push(pageContext.summaryText);
  }

  if (pageContext.actions?.length && !matches.some((match) => match.type === "action")) {
    summaryParts.push(`Principais ações nesta tela: ${pageContext.actions.slice(0, 3).join(", ")}.`);
  }

  summaryParts.push("Se quiser navegar para outra área é só pedir que eu indico o caminho.");

  return {
    text: createVariedReply(summaryParts.join("\n\n")),
  };
};

const MODULE_TUTORIAL_KEYWORDS = [
  "ensinar",
  "ensine",
  "didatico",
  "didática",
  "didatico",
  "treinar",
  "treinamento",
  "aprender",
  "como usar",
  "mexer",
  "módulos",
  "modulos",
  "menu",
  "menus",
  "onboarding",
];

const MODULE_GUIDE_MAP = [
  {
    id: "mural",
    label: "Mural",
    summary: "comunicação interna, publicações e avisos corporativos.",
    firstSteps: ["Abra o Mural", "Filtre por categoria", "Interaja com os avisos relevantes"],
    assistantCommands: ['"abrir mural"', '"fechar mural"'],
  },
  {
    id: "courses",
    label: "Cursos",
    summary: "capacitações e trilhas de aprendizagem com acompanhamento de progresso.",
    firstSteps: ["Abra Cursos", "Escolha uma trilha", "Marque aulas concluídas"],
    assistantCommands: ['"abrir cursos"', '"fechar cursos"'],
  },
  {
    id: "requests",
    label: "Minhas solicitações",
    summary: "execução e acompanhamento de processos e tarefas.",
    firstSteps: ["Clique em Nova solicitação", "Selecione o processo", "Acompanhe status e SLA"],
    assistantCommands: ['"abrir minhas solicitações"'],
  },
  {
    id: "forms",
    label: "Studio • Formulários",
    summary: "criação de formulários com validações e campos dinâmicos.",
    firstSteps: ["Abra Formulários", "Crie o modelo", "Publique e vincule ao processo"],
    assistantCommands: ['"abrir formulários"', '"criar formulário"'],
  },
  {
    id: "processes",
    label: "Studio • Processos",
    summary: "modelagem BPMN, etapas, responsáveis e automações.",
    firstSteps: ["Abra Processos", "Desenhe o fluxo", "Configure gateways e publique"],
    assistantCommands: ['"abrir processos"', '"criar processo"'],
  },
  {
    id: "student-management",
    label: "Gestão de Alunos",
    summary: "cadastro, consulta, turmas, cursos, frequência, histórico, documentos, relatórios e configurações de alunos.",
    firstSteps: ["Abra Gestão de Alunos", "Use Alunos para cadastrar/consultar", "Use Frequência e Histórico para presença"],
    assistantCommands: ['"abrir gestão de alunos"', '"como lançar frequência?"'],
  },
  {
    id: "attendee-management",
    label: "Gestão de Atendidos",
    summary: "cadastro, consulta, turmas, grupos, frequência, histórico, documentos, relatórios e configurações de atendidos.",
    firstSteps: ["Abra Gestão de Atendidos", "Use Atendidos para cadastrar/consultar", "Use Frequência e Histórico para presença"],
    assistantCommands: ['"abrir gestão de atendidos"', '"como lançar frequência?"'],
  },
  {
    id: "project-bank",
    label: "Banco de Projetos",
    summary: "criação de projetos com título, descrição, objetivos, justificativa, público-alvo, orçamento, etapas, fontes, arquivos e PDF.",
    firstSteps: ["Abra Banco de Projetos", "Clique em Novo Projeto", "Preencha campos e salve"],
    assistantCommands: ['"criar novo projeto de capacitação"', '"abrir banco de projetos"'],
  },
  {
    id: "agenda",
    label: "Agenda",
    summary: "compromissos, eventos e SLAs colaborativos.",
    firstSteps: ["Abra Agenda", "Cadastre um evento", "Ajuste horário e participantes"],
    assistantCommands: ['"abrir agenda"', '"agendar reunião amanhã às 10h"'],
  },
  {
    id: "automation-hub",
    label: "Automações e IA",
    summary: "orquestração de robôs, integrações e monitoramento.",
    firstSteps: ["Abra Automações e IA", "Revise integrações", "Acompanhe logs e status"],
    assistantCommands: ['"abrir automações"'],
  },
  {
    id: "file-manager",
    label: "Arquivos",
    summary: "armazenamento, organização e compartilhamento de documentos.",
    firstSteps: ["Abra Arquivos", "Crie ou selecione pasta", "Faça upload e compartilhe"],
    assistantCommands: ['"abrir arquivos"'],
  },
];

const buildModuleTeachingReply = (normalizedPrompt) => {
  if (!containsAnyTokens(normalizedPrompt, MODULE_TUTORIAL_KEYWORDS)) {
    return null;
  }
  const mentionsSystem = /sistema|plataforma|todos|tudo/.test(normalizedPrompt);
  const mentionsModules = /modulo|modulos|m[oó]dulos|menu|menus/.test(normalizedPrompt);
  if (!mentionsSystem && !mentionsModules) {
    return null;
  }

  const modules = MODULE_GUIDE_MAP.map((module, index) => {
    const quickSteps = module.firstSteps.map((step) => `• ${step}`).join("\n");
    const commands = module.assistantCommands.join(" / ");
    return `${index + 1}. **${module.label}** — ${module.summary}\n${quickSteps}\nComandos úteis: ${commands}`;
  });

  return {
    topic: "Treinamento guiado dos módulos",
    text: createVariedReply(
      [
        "Perfeito! Vou te ajudar a ensinar os usuários de forma didática em todos os módulos principais.",
        "Roteiro recomendado para ensinar:",
        modules.join("\n\n"),
        "Dica de facilitação: faça ciclos curtos (demonstrar → usuário pratica → revisão), validando no final de cada módulo.",
        "Se quiser, eu também monto um plano por perfil (operacional, gestor e administrador) com trilha de 30/60/90 minutos.",
      ].join("\n\n")
    ),
    tip: "Você pode pedir o conteúdo por módulo, por exemplo: “me ensine a dar treinamento do módulo de Cursos”.",
  };
};

const STATIC_KNOWLEDGE_BASE = [
  {
    id: "platform-overview",
    title: "Visão geral da plataforma",
    keywords: [
      "bpm",
      "plataforma",
      "intelligent bpm",
      "nash",
      "sistema",
      "backend",
      "frontend",
    ],
    answer:
      "O Intelligent BPM da Nash é composto por um backend Node.js (diretório `backend`) e um frontend React (diretório `frontend`). Ele foi pensado para centralizar processos corporativos, com APIs autenticadas e widgets como o chat embutido na área logada.",
    tip:
      "Toda a base está disponível neste mesmo repositório, permitindo customizações completas nos dois lados da aplicação.",
  },
  {
    id: "email-configuration",
    title: "Envio de e-mails",
    keywords: [
      "email",
      "smtp",
      "notificacao",
      "configuracao de email",
      ".env",
      "mail",
    ],
    answer:
      "As notificações utilizam a camada descrita em `backend/docs/email-debugging.md`. Os dados de SMTP vêm primeiro das variáveis do arquivo `.env` e podem ser ajustados via Configurações → E-mail do servidor. Sempre que algo muda, o transporte é reinicializado automaticamente.",
    tip:
      "Para ambientes novos, copie `backend/.env.example` para `backend/.env` e preencha host, porta, credenciais e remetente padrão.",
  },
  {
    id: "form-builder",
    title: "Construtor de formulários",
    keywords: ["formulario", "form", "campos", "builder", "list"],
    answer:
      "O form builder suporta campos de texto, números e também listas (`list`), que rendem um dropdown com opções pré-definidas. Os grupos e campos podem ser organizados para montar experiências completas de cadastro.",
    tip:
      "Combine campos obrigatórios, validações e regras de visibilidade para personalizar cada processo sem código adicional.",
  },
  {
    id: "form-cocreate-nash-chat",
    title: "Co-criação de campos com a Nash IA no Nash Chat",
    keywords: [
      "co criar",
      "co-criar",
      "nash chat",
      "sugestao de campos",
      "sugestão de campos",
      "adicionar campo",
      "adicionar todos",
      "selecionar varios",
      "selecionar vários",
      "formulario com ia",
    ],
    answer:
      "No fluxo de co-criação, a Nash IA do Nash Chat analisa os campos já existentes e sugere novos grupos/campos complementares. Você pode selecionar tudo, escolher vários itens ou adicionar um campo por vez antes de aplicar no formulário.",
    tip:
      "Peça em linguagem natural, por exemplo: “sugira campos para aprovação com anexos” e depois adicione apenas o que fizer sentido no seu cenário.",
  },
  {
    id: "module-mural-guide",
    title: "Mural (guia rápido)",
    keywords: ["mural", "comunicados", "avisos", "publicacoes internas"],
    answer:
      "O Mural centraliza comunicados internos e atualizações da empresa.\n\nPasso a passo rápido:\n1. Abra o módulo Mural.\n2. Filtre por categoria/tema para focar no que importa.\n3. Leia e acompanhe as publicações recentes.\n4. Volte ao Dashboard quando terminar.",
    tip:
      "Comandos úteis no Nash Chat: “abrir mural” e “fechar mural”.",
  },
  {
    id: "module-courses-guide",
    title: "Cursos (guia rápido)",
    keywords: ["cursos", "treinamentos", "trilha", "capacitação"],
    answer:
      "O módulo de Cursos organiza treinamentos e trilhas de capacitação.\n\nPasso a passo rápido:\n1. Abra Cursos.\n2. Escolha a trilha recomendada para seu perfil.\n3. Conclua as aulas e acompanhe seu progresso.\n4. Revise pendências para manter certificações em dia.",
    tip:
      "Comandos úteis no Nash Chat: “abrir cursos” e “fechar cursos”.",
  },
  {
    id: "module-requests-guide",
    title: "Minhas solicitações (guia rápido)",
    keywords: ["minhas solicitacoes", "solicitações", "requests", "nova solicitacao"],
    answer:
      "Minhas solicitações é o ponto de entrada para iniciar e acompanhar processos.\n\nPasso a passo rápido:\n1. Abra Minhas solicitações.\n2. Clique em “Nova solicitação”.\n3. Escolha o processo e preencha o formulário.\n4. Acompanhe status, prazos e responsáveis pelos filtros.",
    tip:
      "Se quiser, eu te digo exatamente qual filtro usar para cada cenário.",
  },
  {
    id: "module-studio-guide",
    title: "Studio (guia rápido)",
    keywords: ["studio", "formularios", "processos", "bpmn", "modelar fluxo"],
    answer:
      "No Studio você constrói formulários e desenha fluxos BPMN.\n\nPasso a passo rápido:\n1. Em Formulários, crie os campos e grupos.\n2. Em Processos, modele etapas e responsáveis.\n3. Configure gateways/condições.\n4. Publique e teste com uma solicitação piloto.",
    tip:
      "Se pedir, eu monto um checklist de publicação segura em 1 minuto.",
  },
  {
    id: "module-finance-guide",
    title: "Financeiro (guia rápido)",
    keywords: ["financeiro", "pagamentos", "contas a pagar", "contas a receber"],
    answer:
      "O Financeiro concentra lançamentos, custos e acompanhamento operacional.\n\nPasso a passo rápido:\n1. Abra Financeiro.\n2. Revise lançamentos e centros de custo.\n3. Aplique filtros por período/status.\n4. Gere relatórios para conferência e auditoria.",
    tip:
      "Se me disser seu objetivo (pagamento, conciliação ou relatório), eu te passo o caminho direto.",
  },
  {
    id: "rpa-template",
    title: "Integração com RPA",
    keywords: ["rpa", "robos", "price lookup", "automacao"],
    answer:
      "O diretório `rpa/price_lookup_template` traz um template para robôs que consultam preços em provedores externos a partir de dados enviados pelo BPM. Ele reaproveita o padrão usado pelo robô `lancar_pagamento` e já entrega configurações HTTP (`urlTemplate`, `resultsPath`, `mappings`).",
    tip:
      "O resultado consolidado pelo template fica pronto para consumo direto nos fluxos do processo, reduzindo ajustes manuais.",
  },
  {
    id: "gateway-decisions",
    title: "Configuração de gateways",
    keywords: ["gateway", "condicao", "fluxo", "decisao"],
    answer:
      "Nos modelos BPMN é possível adicionar gateways exclusivos para ramificar o fluxo. Assim que um gateway é criado, um diálogo permite definir condições por sequência. A ordem de avaliação é configurável e, se nenhuma condição casar, o fluxo segue pela saída padrão.",
    tip:
      "Você pode reabrir o diálogo dando um duplo clique no gateway para ajustar as regras sempre que precisar.",
  },
  {
    id: "user-hierarchy",
    title: "Hierarquia e supervisão",
    keywords: ["hierarquia", "supervisor", "organograma", "minha equipe"],
    answer:
      "Cada usuário pode ter supervisores vinculados pelo campo **Superior direto**. A relação fica na tabela `user_supervisors` criada pela migration `1757000000001-CreateUserHierarchy`. Isso alimenta abas como Minha equipe e Organograma, além dos endpoints `/users/my-team`, `/users/hierarchy` e `/processes/instances/team`.",
    tip:
      "Não esqueça de rodar `npm run migration:run` para garantir que todas as tabelas de suporte estejam disponíveis antes de usar os recursos de hierarquia.",
  },
];

const buildManualKnowledgeEntries = () =>
  RAW_MANUAL_SECTIONS.filter((section) => section.status === "verified").map((section) => {
    const keywords = [
      section.title,
      section.category,
      section.audience,
      ...(Array.isArray(section.related) ? section.related : []),
    ]
      .filter(Boolean)
      .map((keyword) => keyword.toString());
    const answerParts = [section.summary];
    if (Array.isArray(section.whenToUse) && section.whenToUse.length > 0) {
      answerParts.push(["Quando utilizar:", ...section.whenToUse.map((item) => `• ${item}`)].join("\n"));
    }
    if (Array.isArray(section.steps) && section.steps.length > 0) {
      const highlightedSteps = section.steps;
      answerParts.push(
        [
          "Passos iniciais:",
          ...highlightedSteps.map((step, index) => `${index + 1}. ${step}`),
        ].join("\n"),
      );
    }
    if (Array.isArray(section.related) && section.related.length > 0) {
      answerParts.push(`Relacionado: ${section.related.join(", ")}`);
    }
    return {
      id: `manual-${section.id}`,
      title: `Manual: ${section.title}`,
      keywords,
      answer: answerParts.filter(Boolean).join("\n\n"),
      tip: Array.isArray(section.tips) ? section.tips[0] : null,
    };
  });

const buildGuideKnowledgeEntries = (guides, { prefix, sourceLabel, tipKey = "insights" }) =>
  guides
    .filter((guide) => guide && typeof guide === "object")
    .map((guide) => {
      const keywords = [guide.title, sourceLabel, ...(guide.keywords || [])]
        .filter(Boolean)
        .map((keyword) => keyword.toString());
      const answerParts = [guide.summary];
      if (Array.isArray(guide.steps) && guide.steps.length > 0) {
        const highlightedSteps = guide.steps.slice(0, 3);
        answerParts.push(
          [
            "Como aproveitar:",
            ...highlightedSteps.map((step, index) => `${index + 1}. ${step}`),
          ].join("\n"),
        );
      }
      if (Array.isArray(guide.promptExamples) && guide.promptExamples.length > 0) {
        const prompts = guide.promptExamples.slice(0, 3).join("; ");
        answerParts.push(`Sugestões de prompt: ${prompts}.`);
      }
      const tipSource = Array.isArray(guide[tipKey]) ? guide[tipKey] : null;
      return {
        id: `${prefix}-${guide.id}`,
        title: `${sourceLabel}: ${guide.title}`,
        keywords,
        answer: answerParts.filter(Boolean).join("\n\n"),
        tip: tipSource && tipSource.length > 0 ? tipSource[0] : null,
      };
    });

const KNOWLEDGE_BASE = [
  ...buildManualKnowledgeEntries(),
  ...buildGuideKnowledgeEntries(FORM_ASSISTANT_GUIDES, {
    prefix: "form-guide",
    sourceLabel: "Assistente de formulários",
  }),
  ...buildGuideKnowledgeEntries(PROCESS_ASSISTANT_GUIDES, {
    prefix: "process-guide",
    sourceLabel: "Assistente de processos",
  }),
  ...buildGuideKnowledgeEntries(
    STUDIO_ASSISTANT_GUIDES.map((guide) => ({ ...guide, insights: guide.highlights || [] })),
    {
      prefix: "studio-guide",
      sourceLabel: "Assistente IA do Studio",
    },
  ),
  ...STATIC_KNOWLEDGE_BASE,
];

const FALLBACK_RESPONSES = [
  "Sou a Nash IA! Posso explicar módulos do Intelligent BPM, configurações de e-mail, formulários, RPA e governança. Tente perguntar de forma direta sobre o assunto desejado.",
  "Ainda não encontrei detalhes sobre esse tema no repositório. Experimente mencionar palavras-chave como formulário, gateway, RPA ou hierarquia para que eu busque a orientação correta.",
];

const ENRICHED_KNOWLEDGE = KNOWLEDGE_BASE.map((entry) => ({
  ...entry,
  tokenSet: buildKeywordSet(entry.keywords),
}));

const pickFallback = () =>
  createVariedReply(
    FALLBACK_RESPONSES[Math.floor(Math.random() * FALLBACK_RESPONSES.length)]
  );

const SYSTEM_CAPABILITY_KEYWORDS = [
  "o que voce faz",
  "o que você faz",
  "o que consegue",
];


const buildRealtimeMenuReply = (normalizedPrompt = "", pageContext = null) => {
  const wantsMenu = /menu|menus|modulo|modulos|m[oó]dulo|sistema|plataforma|tela|opcoes|opções/.test(normalizedPrompt);
  if (!wantsMenu) return null;
  const menuItems = Array.isArray(pageContext?.menu?.items) ? pageContext.menu.items : [];
  if (!menuItems.length) return null;
  const visiblePaths = menuItems
    .map((item) => item.path || item.label)
    .filter(Boolean)
    .slice(0, 18);
  const title = pageContext?.title ? `Tela atual: ${pageContext.title}.` : "";
  return {
    topic: "Menu em tempo real",
    text: [
      title,
      `Menu disponível agora: ${visiblePaths.join("; ")}.`,
      "Se terminar com ?, respondo como pergunta; sem ?, posso executar comandos como abrir agenda ou abrir gestão de alunos.",
    ].filter(Boolean).join("\n\n"),
    skipStructure: true,
  };
};

const buildCapabilityOverviewReply = (normalizedPrompt = "") => {
  if (!containsAnyTokens(normalizedPrompt, SYSTEM_CAPABILITY_KEYWORDS)) {
    return null;
  }

  const quickCommands = [
    '"abrir agenda"',
    '"criar evento amanhã às 10h"',
    '"abrir financeiro"',
    '"criar formulário de cadastro"',
    '"me ensine os módulos do sistema"',
  ].join(" • ");

  return {
    topic: "Como a Nash IA pode ajudar",
    text: createVariedReply(
      [
        "Posso abrir módulos, te guiar no uso e ajudar a criar itens (evento, formulário e processo).",
        `Exemplos: ${quickCommands}.`,
      ].join("\n\n")
    ),
  };
};

const buildSmartFallbackReply = (normalizedPrompt = "") => {
  if (!normalizedPrompt) {
    return pickFallback();
  }
  const promptTokens = normalizedPrompt.split(" ").filter(Boolean);
  const rankedModules = MODULE_GUIDE_MAP.map((module) => {
    const moduleTokens = buildKeywordSet([
      module.label,
      module.summary,
      ...(module.firstSteps || []),
      ...(module.assistantCommands || []),
    ]);
    let score = 0;
    promptTokens.forEach((token) => {
      if (moduleTokens.has(token)) {
        score += 1;
      }
    });
    return { module, score };
  })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  if (!rankedModules.length) {
    return pickFallback();
  }

  return createVariedReply("Entendi. Me diga exatamente o que você quer que eu faça.");
};

const buildActionOnlyResponse = (reply = {}, action = null) => {
  const targetAction = reply?.action || action || null;
  if (!targetAction) {
    return reply;
  }
  const successText = targetAction.successMessage || "";
  const replyText = typeof reply?.text === "string" ? reply.text : "";
  const finalText = successText || replyText || pickFallback();
  return {
    ...reply,
    action: targetAction,
    text: createVariedReply(finalText, { disableConnectors: true }),
  };
};

const structureReplyPayload = (reply = {}, action = null) => {
  const text = typeof reply?.text === "string" ? reply.text.trim() : "";
  if (!text) {
    return reply;
  }

  const structuredText = buildStructuredReplyText({
    text,
    topic: reply.topic || reply.action?.label || action?.label || "",
    highlights: reply.tip ? [reply.tip] : [],
  });

  return {
    ...reply,
    text: structuredText,
  };
};

const buildFinalReply = (reply, action) =>
  buildActionOnlyResponse(structureReplyPayload(reply, action), action);

const pickFirstSentences = (text = "", limit = 2) => {
  const sentences = text
    .split(/(?<=[.!?])\s+/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
  return sentences.slice(0, limit).join(" ");
};

const pickFirstBullet = (text = "") => {
  const lines = text
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);
  const bulletLine = lines.find((line) => /^[-•]/.test(line));
  return bulletLine ? bulletLine.replace(/^[-•]\s*/, "") : "";
};

const buildConciseCommandReply = (text = "", userName = "") => {
  const headline = pickFirstSentences(text, 2);
  const bullet = pickFirstBullet(text);
  const summaryParts = [headline, bullet ? `• ${bullet}` : ""].filter(Boolean);
  return summaryParts.join(summaryParts.length > 1 ? "\n" : " ").trim();
};

const extractMathExpression = (prompt = "") => {
  const sanitized = (prompt || "")
    .replace(/,/g, ".")
    .replace(/[^0-9+\-*/^%().\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const hasNumber = /\d/.test(sanitized);
  const hasOperator = /[+\-*/^%]/.test(sanitized);
  const isValidCharacters = /^[0-9+\-*/^%().\s]+$/.test(sanitized);

  if (!sanitized || !hasNumber || !hasOperator || !isValidCharacters) {
    return "";
  }

  return sanitized;
};

const evaluateMathExpression = (expression = "") => {
  const target = expression.trim();
  if (!target) return null;

  const result = evaluateArithmeticExpression(target);
  if (result === null) {
    return null;
  }
  return Math.abs(result) < 1e-6 ? 0 : Number(result.toFixed(6));
};

const buildMathReply = (prompt = "") => {
  const expression = extractMathExpression(prompt);
  if (!expression) {
    return null;
  }

  const result = evaluateMathExpression(expression);
  if (result === null) {
    return null;
  }

  const friendlyExpression = expression.replace(/\s+/g, " ");
  const resultText = Number.isInteger(result) ? result.toString() : result.toLocaleString("pt-BR");
  return {
    text: `${friendlyExpression} = ${resultText}`,
    topic: "",
    skipStructure: true,
    skipPersonalization: true,
  };
};

const WEB_SEARCH_KEYWORDS = [
  "pesquise",
  "pesquisa",
  "buscar",
  "busque",
  "procure",
  "internet",
  "google",
  "web",
  "achou",
  "encontre",
  "encontrar",
];

const shouldAttemptWebSearch = (normalizedPrompt = "") =>
  Boolean(normalizedPrompt) && WEB_SEARCH_KEYWORDS.some((keyword) => normalizedPrompt.includes(keyword));

const buildWebSearchReply = async (prompt = "", normalizedPrompt = "", options = {}) => {
  if (!shouldAttemptWebSearch(normalizedPrompt)) {
    return null;
  }
  return {
    text: createVariedReply(
      "Para pesquisar na internet, preciso de uma integração de busca externa configurada. Por enquanto respondo com base no Manual do Usuário, no menu em tempo real e nos comandos do sistema."
    ),
    topic: "Pesquisa na internet",
    skipStructure: true,
  };
};

const enhanceDetailedReply = (text = "", userName = "") => (text || "").trim();

const GRATITUDE_KEYWORDS = ["obrigado", "obrigada", "valeu", "agradeco", "agradeço"];
const GREETING_KEYWORDS = ["bom dia", "boa tarde", "boa noite", "ola", "olá", "oi", "e ai", "e aí"];
const WELLBEING_KEYWORDS = ["tudo bem", "como vai", "como voce esta", "como você está", "como ce ta"];

const buildSmallTalkReply = (prompt = "", normalizedPrompt = "", options = {}) => {
  if (!normalizedPrompt) {
    return null;
  }

  const tokenCount = normalizedPrompt.split(" ").filter(Boolean).length;
  if (tokenCount > 8) {
    return null;
  }

  const firstName = extractFirstName(options?.user?.name || "");
  const namePrefix = firstName ? `${firstName}, ` : "";

  if (GRATITUDE_KEYWORDS.some((keyword) => normalizedPrompt.includes(keyword))) {
    return {
      text: createVariedReply(
        `${namePrefix}eu que agradeço! Se quiser, sigo ajudando a próxima etapa ou deixo um lembrete aqui.`,
        { disableConnectors: true }
      ),
      topic: "Agradecimento",
    };
  }

  if (WELLBEING_KEYWORDS.some((keyword) => normalizedPrompt.includes(keyword))) {
    return {
      text: createVariedReply(
        `${namePrefix}estou ótima e pronta para ajudar. O que você quer resolver primeiro?`,
        { disableConnectors: true }
      ),
      topic: "Saudação",
    };
  }

  if (GREETING_KEYWORDS.some((keyword) => normalizedPrompt.includes(keyword))) {
    return {
      text: createVariedReply(
        `${namePrefix}oi! Que tal me contar o próximo passo que quer automatizar ou entender?`,
        { disableConnectors: true }
      ),
      topic: "Saudação",
    };
  }

  return null;
};

const CLARITY_KEYWORDS = [
  "mais claro",
  "melhor",
  "explica melhor",
  "não entendi",
  "nao entendi",
  "resumido",
  "passo a passo",
];

const buildClarityReply = (normalizedPrompt = "") => {
  if (!normalizedPrompt) {
    return null;
  }
  const needsClarity = CLARITY_KEYWORDS.some((keyword) => normalizedPrompt.includes(keyword));
  if (!needsClarity) {
    return null;
  }
  return {
    topic: "Resposta mais clara",
    text: createVariedReply(
      [
        "Perfeito — vou responder de forma mais direta daqui para frente.",
        "Formato que vou usar:",
        "1. Objetivo da tela/módulo em uma frase.",
        "2. Passo a passo curto (3 a 5 passos).",
        "3. Erro comum para evitar.",
        "4. Próximo comando que você pode me pedir.",
      ].join("\n")
    ),
  };
};

const CLOSE_HELP_KEYWORDS = [
  "fechar",
  "feche",
  "encerrar",
  "encerre",
  "sair",
  "finalizar",
  "finalize",
  "terminar",
  "termine",
  "concluir",
  "conclua",
];

const CLOSE_TARGET_KEYWORDS = [
  "aba",
  "abas",
  "mural",
  "cursos",
  "dashboard",
  "solicitacoes",
  "solicitações",
  "agenda",
  "manual",
  "crm",
  "financeiro",
  "estoque",
  "arquivos",
  "studio",
  "formularios",
  "formulários",
  "processos",
];

const buildAmbiguousCloseReply = (normalizedPrompt = "", action = null) => {
  if (action) {
    return null;
  }
  if (!normalizedPrompt) {
    return null;
  }
  const hasCloseIntent = CLOSE_HELP_KEYWORDS.some((keyword) => normalizedPrompt.includes(keyword));
  if (!hasCloseIntent) {
    return null;
  }
  const hasKnownTarget = CLOSE_TARGET_KEYWORDS.some((keyword) => normalizedPrompt.includes(keyword));
  if (hasKnownTarget) {
    return null;
  }

  return {
    topic: "Confirmação de fechamento",
    text: createVariedReply(
      [
        "Não entendi exatamente o que você quer fechar.",
        "Você quer fechar todas as abas, ou alguma aba específica?",
        "Se quiser, tente algo como: “fechar todas as abas”, “fechar financeiro” ou “fechar agenda”.",
      ].join("\n")
    ),
  };
};

const personalizeReplyForUser = (reply = {}, prompt = "", userName = "") => {
  if (!reply?.text || reply?.skipPersonalization) {
    return reply;
  }
  const intent = classifyPromptIntent(prompt);
  const personalizedText =
    intent === "command"
      ? buildConciseCommandReply(reply.text, userName)
      : enhanceDetailedReply(reply.text, userName);
  return {
    ...reply,
    text: personalizedText,
  };
};

export const getAssistantReply = async (prompt, options = {}) => {
  const { dataset = null, datasetStatus = "idle", user = null, pageContext = null } = options;
  const normalizedPrompt = normalizeText(prompt);
  const questionIntent = isQuestionPrompt(prompt);
  const action = questionIntent ? null : resolveAssistantAction(prompt);
  const finalizeReply = (replyCandidate, targetAction = action) => {
    const builtReply = replyCandidate?.skipStructure
      ? replyCandidate
      : buildFinalReply(replyCandidate, targetAction);
    return personalizeReplyForUser(builtReply, prompt, user?.name || "");
  };
  if (!normalizedPrompt) {
    return finalizeReply({
      text: createVariedReply(
        "Posso detalhar como configurar o sistema, gerar formulários ou usar o RPA. Qual dúvida você quer tirar primeiro?"
      ),
      action,
    });
  }
  const smallTalkReply = buildSmallTalkReply(prompt, normalizedPrompt, { user });
  if (smallTalkReply) {
    return { ...finalizeReply(smallTalkReply, action), localOnly: true };
  }
  const clarityReply = buildClarityReply(normalizedPrompt);
  if (clarityReply) {
    return { ...finalizeReply(clarityReply, action), localOnly: true };
  }
  const ambiguousCloseReply = buildAmbiguousCloseReply(normalizedPrompt, action);
  if (ambiguousCloseReply) {
    return finalizeReply(ambiguousCloseReply, action);
  }
  if (/novo projeto|criar projeto|fazer projeto/.test(normalizedPrompt)) {
    return finalizeReply({
      text: "Caminho: Studio → Banco de Projetos → Novo Projeto. Envie título, objetivo, público-alvo, prazo e orçamento para eu estruturar o projeto.",
      action,
      skipStructure: true,
    });
  }
  if (/gestao de atendidos|gestão de atendidos|modulo de atendidos|módulo de atendidos/.test(normalizedPrompt)) {
    return finalizeReply({
      text: "Caminho: Gestão de Atendidos. Lá você gerencia dashboard, turmas, grupos, cadastro/consulta de atendidos, frequência, histórico, documentos, relatórios e configurações.",
      action,
      skipStructure: true,
    });
  }
  if (/gestao de alunos|gestão de alunos|modulo de alunos|módulo de alunos/.test(normalizedPrompt)) {
    return finalizeReply({
      text: "Caminho: Gestão de Alunos. Lá você gerencia dashboard, turmas, cursos, cadastro/consulta de alunos, frequência, histórico, documentos, relatórios e configurações.",
      action,
      skipStructure: true,
    });
  }
  const realtimeMenuReply = buildRealtimeMenuReply(normalizedPrompt, pageContext);
  if (realtimeMenuReply) {
    return { ...finalizeReply(realtimeMenuReply, action), localOnly: true };
  }
  const capabilityOverviewReply = buildCapabilityOverviewReply(normalizedPrompt);
  if (capabilityOverviewReply) {
    return finalizeReply(capabilityOverviewReply, action);
  }
  const datasetReply = getDatasetReply(prompt, normalizedPrompt, dataset, {
    datasetStatus,
    user,
  });
  if (datasetReply) {
    const enrichedDatasetReply = {
      ...datasetReply,
      action: datasetReply.action || action || null,
    };
    return { ...finalizeReply(enrichedDatasetReply, action), localOnly: true };
  }
  const pageContextReply = buildPageContextReply(prompt, normalizedPrompt, pageContext, { action });
  if (pageContextReply) {
    return finalizeReply(pageContextReply, action);
  }
  const blueprintReply = resolveBlueprintReply(prompt, normalizedPrompt);
  if (blueprintReply) {
    const enrichedBlueprint = {
      ...blueprintReply,
      action: blueprintReply.action || action || null,
    };
    return finalizeReply(enrichedBlueprint, action);
  }
  const collaborationReply = buildCollaborationOnlyReply(prompt, normalizedPrompt);
  if (collaborationReply) {
    const enrichedCollaboration = {
      ...collaborationReply,
      action: collaborationReply.action || action || null,
    };
    return finalizeReply(enrichedCollaboration, action);
  }
  const mathReply = buildMathReply(prompt);
  if (mathReply) {
    return { ...finalizeReply(mathReply, action), localOnly: true };
  }
  const moduleTeachingReply = buildModuleTeachingReply(normalizedPrompt);
  if (moduleTeachingReply) {
    return finalizeReply(moduleTeachingReply, action);
  }
  const webReply = await buildWebSearchReply(prompt, normalizedPrompt, { user });
  if (webReply) {
    return finalizeReply(webReply, action);
  }
  const promptTokens = normalizedPrompt.split(" ").filter(Boolean);
  const ranked = ENRICHED_KNOWLEDGE.map((entry) => {
    let score = 0;
    entry.keywords.forEach((keyword) => {
      if (normalizedPrompt.includes(normalizeText(keyword))) {
        score += Math.max(2, keyword.split(" ").length);
      }
    });
    promptTokens.forEach((token) => {
      if (entry.tokenSet.has(token)) {
        score += 1;
      }
    });
    return { entry, score };
  })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  const bestMatch = ranked[0];
  if (!bestMatch || bestMatch.score < 2) {
    return finalizeReply({ text: buildSmartFallbackReply(normalizedPrompt) }, action);
  }

  const { entry } = bestMatch;
  const parts = [entry.answer];
  if (entry.tip) {
    parts.push(entry.tip);
  }
  return finalizeReply(
    { text: createVariedReply(parts.join("\n\n")), topic: entry.title, tip: entry.tip },
    action
  );
};
